/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ICache, ICacheKey } from '@msdyn365-commerce/cache-internal';
import { getPerformanceUtils, ICoreContext, IModule, msdyn365Commerce } from '@msdyn365-commerce/core-internal';
import { asSystemMetadata, EXCEPTION_NOMODULEBINDER, LogLevel } from '@msdyn365-commerce/telemetry-internal';
import React from 'react';

import { IMSDyn365CommerceExtension } from '../app-initialization/models';
import { getModuleBinder, getModuleName } from '../utils/helpers';

export interface IModuleWithView extends IModule {
    View: React.FunctionComponent;
}

const getViewBinder = (viewLookupKey: string) => {
    const view = ((msdyn365Commerce as unknown) as IMSDyn365CommerceExtension).viewBinder(viewLookupKey);
    if (view) {
        return { key: viewLookupKey, view };
    }
    return undefined;
};

/**
 * View will be read in following hierarchy
 *  1) __local__|__local__|themes|theme1|views|product-tile  OR  @msdyn-365-commerce-modules|theme1|modules|theme1|views|product-tile
 *  2) __local__|__local__|modules|product-tile|product-tile  OR  @msdyn-365-commerce-modules|product-tile|modules|product-tile|product-tile
 */
const viewLookupFunc = (moduleBinder, themeBinder) =>
    themeBinder &&
    (getViewBinder(`__local__|__local__|themes|${themeBinder.name}|views|${moduleBinder.name}`) ||
        getViewBinder(`${themeBinder.moduleNamespace}|${themeBinder.packageName}|modules|${themeBinder.name}|views|${moduleBinder.name}`));

/**
 * View will be read from the module
 *  __local__|__local__|modules|product-tile|product-tile  OR  @msdyn-365-commerce-modules|product-tile|modules|product-tile|product-tile
 */
const defaultViewLookupFunc = moduleBinder => {
    const parentModuleBinder = getModuleBinder(getModuleName(moduleBinder.parentDefinitionPath));

    return (
        getViewBinder(`${moduleBinder.moduleNamespace}|${moduleBinder.packageName}|modules|${moduleBinder.name}|${moduleBinder.name}`) ||
        (parentModuleBinder &&
            getViewBinder(
                `${parentModuleBinder.moduleNamespace}|${parentModuleBinder.packageName}|modules|${parentModuleBinder.name}|${parentModuleBinder.name}`
            ))
    );
};

const getView = (moduleBinder, themeBinder, context: ICoreContext) => {
    if (!moduleBinder) {
        return { view: null };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cache: ICache = (msdyn365Commerce as any).getAppCache(context.actionContext.requestContext);
    const viewCacheKey: ICacheKey = {
        key: `${moduleBinder.name}|${themeBinder ? themeBinder.name : 'no-theme'}`,
        typeName: '__ViewCacheKey__'
    };
    const viewLookupKey = cache.getValue<string>(viewCacheKey);

    const actionContextData: any = context.actionContext;
    if (viewLookupKey) {
        !msdyn365Commerce.isBrowser && actionContextData.requestCache.put(viewCacheKey, { item: viewLookupKey });
        return getViewBinder(viewLookupKey) || { view: null };
    }

    let viewLookup = viewLookupFunc(moduleBinder, themeBinder);

    // If no component found in the theme, search in parent theme (in case of inherited theme)
    if (!viewLookup && themeBinder) {
        const parentThemeName = getModuleName(themeBinder.parentDefinitionPath);
        const parentThemeBinder = getModuleBinder(parentThemeName);
        viewLookup = viewLookupFunc(moduleBinder, parentThemeBinder);
    }

    // If no view file found, fall back to module's default view
    if (!viewLookup) {
        viewLookup = defaultViewLookupFunc(moduleBinder);
    }

    if (viewLookup) {
        cache.put(viewCacheKey, { item: viewLookup.key });
        actionContextData.requestCache.put(viewCacheKey, { item: viewLookup.key });
        return viewLookup;
    }

    return { view: null };
};

const renderViewForModule: React.FC<IModule> = <T extends IModule>(props: T): React.ReactElement | null => {
    props.telemetry.log(LogLevel.Debug, `rendering view-'${props.typeName}' with id-'${props.id}'`);
    const moduleBinder = getModuleBinder(props.typeName);
    if (!moduleBinder) {
        props.context.telemetry.log(LogLevel.Error, EXCEPTION_NOMODULEBINDER, { values: [asSystemMetadata(props.typeName)] });
        return null;
    }

    const themeBinder = getModuleBinder(props.context.actionContext.requestContext.params.theme);
    const { view } = getView(moduleBinder, themeBinder, props.context);

    if (!view) {
        return props.context.actionContext.requestContext.params.isDebug ? (
            <section>
                <h2>View not found - location searched at</h2>
                <p>{`views/${moduleBinder.name}.view.tsx`}</p>
                <p>
                    {themeBinder
                        ? `views/${themeBinder.name}/${moduleBinder.name}.view.tsx`
                        : `No theme [${props.context.actionContext.requestContext.params.theme}] found at local/src`}
                </p>
                <p>
                    {themeBinder
                        ? `${
                              themeBinder.moduleNamespace !== '__local__'
                                  ? `${themeBinder.moduleNamespace}/${themeBinder.packageName}/`
                                  : ''
                          }modules/${themeBinder.name}/views/${moduleBinder.name}.view.tsx`
                        : `No theme [${props.context.actionContext.requestContext.params.theme}] found at node_modules`}
                </p>
                <p>{`${
                    moduleBinder.moduleNamespace !== '__local__' ? `${moduleBinder.moduleNamespace}/${moduleBinder.packageName}/` : ''
                }modules/${moduleBinder.name}/${moduleBinder.name}.view.tsx`}</p>
            </section>
        ) : null;
    }

    const Component = view.component;
    // @ts-ignore
    if (props.addChunk) {
        // @ts-ignore
        props.addChunk(view.cn);
    }
    return <Component {...props} />;
};

export const renderView: React.FC<IModule> = <T extends IModule>(props: T): React.ReactElement | null => {
    const performanceUtils = getPerformanceUtils(msdyn365Commerce, props.context.actionContext);

    const renderId = performanceUtils?.performanceUtilsPackage.Random.Guid.generateGuid();

    performanceUtils?.performanceUtilsPackage.runPreRenderViewPerformanceHook({
        moduleId: props.id,
        moduleTypeName: props.typeName,

        renderId: renderId,

        internalCache: performanceUtils.cache
    });

    const node = renderViewForModule(props);

    performanceUtils?.performanceUtilsPackage.runPostRenderViewPerformanceHook({
        moduleId: props.id,
        moduleTypeName: props.typeName,

        renderId: renderId,

        internalCache: performanceUtils.cache
    });

    return node;
};
